<script>
import { getCurrentPath } from '@js/utilities';
import { useSearchModeStore } from '@js/stores/search/searchModeStore';
import Modal from '@components/Shared/UhModal';
import StackedPropertyTypeFilter from './Property/MobileSearchFilters/StackedPropertyTypeFilter.vue';
import StackedEnquiredPropertyFilter from './Property/MobileSearchFilters/StackedEnquiredPropertyFilter.vue';
import { PROPERTY_TYPES } from '@components/Property/MobileSearchFilters/constants.ts';
import { applySpareRoomsExclusivity } from '@components/Property/MobileSearchFilters/helpers.ts';

export default {
  components: {
    StackedEnquiredPropertyFilter,
    StackedPropertyTypeFilter,
    Modal,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    currentPropertyType: {
      type: [String, Array],
      required: false,
      default: null,
    },
    currentUniversity: {
      type: Object,
      required: false,
      default: null,
    },
    currentArea: {
      type: Object,
      required: false,
      default: null,
    },
    currentEnquiredProperties: {
      type: Boolean,
      default: false,
    },
    universities: {
      type: Array,
      required: false,
      default: () => [],
    },
    areas: {
      type: Array,
      required: false,
      default: () => [],
    },
    enablePrivateHallsFilter: {
      type: Boolean,
      default: false,
    },
    enablePropertyTypeFilters: {
      type: Boolean,
      default: false,
    },
    enableSpareRoomFunctionality: {
      type: Boolean,
      default: false,
    },
    enableStudentEnquiriesCta: {
      type: Boolean,
      default: false,
    },
    hasEnquiredProperties: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    propertyType: null,
    selectedUni: null,
    selectedArea: null,
    enquiredProperties: false,
  }),
  computed: {
    blendedSearch() {
      return useSearchModeStore().blendedSearch;
    },
    propertyTypes() {
      return PROPERTY_TYPES.map((type) => {
        let visible = type.visible;

        if (type.id === 'private-halls') {
          visible = this.enablePrivateHallsFilter;
        } else if (type.id === 'spare-rooms') {
          visible = this.enableSpareRoomFunctionality;
        }

        return {
          ...type,
          visible,
        };
      });
    },
    submitLabel() {
      const currentPath = this.getCurrentPath();
      const enquiredProperties = this.enquiredProperties;
      const enableStudentEnquiriesCta = this.enableStudentEnquiriesCta;

      if (
        !enquiredProperties &&
        enableStudentEnquiriesCta &&
        currentPath &&
        currentPath === '/student-accommodation'
      ) {
        return 'Resume search';
      }

      return 'Show results';
    },
    showUniversities() {
      return this.universities && this.universities.length > 0;
    },
    showAreas() {
      return this.areas && this.areas.length > 0;
    },
  },
  watch: {
    currentUniversity(newVal) {
      this.selectedUni = newVal ? newVal.name : 'All Universities';
    },
    currentArea(newVal) {
      this.selectedArea = newVal ? newVal.name : null;
    },
    currentEnquiredProperties(newValue) {
      this.enquiredProperties = newValue ?? false;
    },
  },
  mounted() {
    this.selectedUni =
      this.$props.currentUniversity?.name ?? 'All Universities';

    if (this.$props.currentArea) {
      this.selectedArea = this.$props.currentArea.name;
    }

    if (this.currentEnquiredProperties) {
      this.enquiredProperties = this.currentEnquiredProperties;
    }
  },
  methods: {
    getCurrentPath,
    handleClear() {
      this.propertyType = null;
      this.selectedUni = null;
      this.selectedArea = null;
      // If the user is currently in Enquiries, clearing filters should keep
      // them in Enquiries (it should not toggle enquiries off).
      this.enquiredProperties = !!this.currentEnquiredProperties;

      this.$emit('set-property-type', null);
      this.$emit('set-university', null);
      this.$emit('set-area', null);
      this.$emit('set-enquired-properties', !!this.currentEnquiredProperties);

      this.handleClose();
    },
    handleClose() {
      this.$emit('close');
    },
    handlePropertyTypeChange(propertyType) {
      // Only remove the property type if it is deliberately cleared by the user.
      if (propertyType === undefined) {
        return;
      }

      // Handle mutual exclusivity: spare-rooms is exclusive with all other types.
      // Blended results list spare rooms alongside every other type, so the rule lapses there.
      if (propertyType && !this.blendedSearch && Array.isArray(propertyType)) {
        propertyType = applySpareRoomsExclusivity(propertyType);
      }

      this.propertyType = propertyType;
      this.$emit('set-property-type', this.propertyType);
    },
    handleUniversityChange(university) {
      this.selectedUni = university.name;

      this.$emit('set-university', university);
    },
    handleAreaChange(area) {
      this.selectedArea = area.name;
      this.$emit('set-area', area);
    },
    handleEnquiredPropertiesChange(checked) {
      this.enquiredProperties = checked;

      this.$emit('set-enquired-properties', checked);
    },
  },
};
</script>

<template>
  <Modal
    :show="show"
    body-classes="tw-h-[575px] tw-overflow-y-scroll"
    footer-classes="tw-border-t tw-border-[#eeeeee] tw-p-[24px]"
    modal-classes="tw-rounded-2xl"
    height="fit-content"
    max-height="90vh"
    width="600px"
    dusk="expanded-properties-search-filter-modal"
    data-testid="expanded-properties-search-filter-modal"
    @close="handleClose"
  >
    <template #header>
      <span class="tw-font-semibold tw-text-primary-grey">More filters</span>
    </template>

    <template #body>
      <div
        v-if="enableStudentEnquiriesCta && hasEnquiredProperties"
        class="tw-mb-6"
      >
        <h3 class="tw-m-0 tw-p-0 tw-text-base tw-font-semibold">Enquiries</h3>
        <p class="tw-m-0 tw-p-0 tw-text-[14px]">
          This will override all current filters
        </p>
      </div>

      <div
        v-if="enableStudentEnquiriesCta && hasEnquiredProperties"
        class="tw-border-b-solid tw-border-b tw-border-b-gray-100"
      >
        <StackedEnquiredPropertyFilter
          :checked="enquiredProperties"
          @change="handleEnquiredPropertiesChange"
        />
      </div>
      <div
        class="tw-border-b-solid tw-border-b tw-border-b-gray-100 tw-pb-5"
        :class="{
          'tw-pt-5': enableStudentEnquiriesCta && hasEnquiredProperties,
        }"
      >
        <p class="tw-text-base tw-font-semibold tw-leading-6">Property type</p>
        <StackedPropertyTypeFilter
          multiple
          compact
          hide-heading
          :active="currentPropertyType"
          :enable-dark-blue-accent="enablePropertyTypeFilters"
          :property-types="propertyTypes"
          @change="handlePropertyTypeChange"
        />
      </div>
      <div
        v-if="showUniversities"
        class="tw-border-b-solid tw-relative tw-mt-5 tw-border-b tw-border-b-gray-100 tw-pb-5"
      >
        <div
          v-if="enquiredProperties && enableStudentEnquiriesCta"
          class="tw-absolute tw-left-0 tw-top-0 tw-z-10 tw-h-full tw-w-full tw-bg-white/80"
        ></div>
        <p class="tw-text-base tw-font-semibold tw-leading-6">
          Nearby university
        </p>

        <div class="tw-flex tw-flex-col tw-gap-2">
          <div
            v-for="(university, index) in universities"
            :key="`uni-${index}`"
            class="tw-flex tw-flex-row tw-justify-between"
            v-bind="university"
            @click="handleUniversityChange(university)"
          >
            <div class="tw-relative tw-flex tw-gap-x-3">
              <div class="tw-flex tw-items-center">
                <input
                  v-model="selectedUni"
                  :value="university.name"
                  name="uni-group"
                  type="radio"
                  class="tw-w-4 tw-border-gray-300 tw-text-[#00A2E1] tw-outline-none tw-ring-0 tw-ring-offset-0 tw-ring-offset-transparent focus:tw-ring-0 focus:tw-ring-offset-transparent"
                />
              </div>
              <div class="tw-text-sm tw-leading-6">
                <label class="tw-text-base tw-font-[400]" for="comments">{{
                  university.name
                }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showAreas" class="tw-relative tw-mt-5">
        <div
          v-if="enquiredProperties && enableStudentEnquiriesCta"
          class="tw-absolute tw-left-0 tw-top-0 tw-z-10 tw-h-full tw-w-full tw-bg-white/80"
        ></div>
        <p class="tw-text-base tw-font-semibold tw-leading-6">Areas</p>

        <div class="tw-flex tw-flex-col tw-gap-2">
          <div
            v-for="(area, index) in areas"
            :key="`area-${index}`"
            :dusk="`area-${index}`"
            class="tw-flex tw-flex-row tw-justify-between"
            v-bind="area"
            @click="handleAreaChange(area)"
          >
            <div class="tw-relative tw-flex tw-gap-x-3">
              <div class="tw-flex tw-items-center">
                <input
                  v-model="selectedArea"
                  :dusk="`area-input-${index}`"
                  :value="area.name"
                  class="tw-w-4 tw-border-gray-300 tw-text-[#00A2E1] tw-outline-none tw-ring-0 tw-ring-offset-0 tw-ring-offset-transparent focus:tw-ring-0 focus:tw-ring-offset-transparent"
                  name="uni-group"
                  type="radio"
                />
              </div>
              <div class="tw-text-sm tw-leading-6">
                <label
                  class="tw-text-base tw-font-[400]"
                  :dusk="`area-name-${index}`"
                  for="comments"
                  >{{ area.name }}</label
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="tw-inline-flex tw-items-center tw-justify-between">
        <span
          class="px-0 tw-ease tw-relative tw-right-auto tw-top-0 tw-flex tw-w-[158px] tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-[#5C6A75] tw-bg-white tw-px-[20px] tw-py-[10px] tw-text-center tw-text-[16px] tw-font-semibold tw-leading-[32px] tw-text-[#5C6A75] tw-transition-all tw-duration-300 hover:tw-cursor-pointer hover:tw-border-[#00A2E1] hover:tw-bg-[#d9e4e9] focus:tw-border-[#00A2E1]"
          dusk="expanded-properties-search-filter-modal-clear"
          @click="handleClear"
          >Clear</span
        >
        <span
          class="px-0 tw-ease tw-relative tw-right-auto tw-top-0 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-bg-orange-500 tw-px-[20px] tw-py-[10px] tw-text-center tw-text-[16px] tw-font-semibold tw-leading-[32px] tw-text-white tw-transition-all tw-duration-300 hover:tw-cursor-pointer hover:tw-bg-refreshPrimaryBg-dark"
          dusk="expanded-properties-search-filter-modal-show-results"
          data-testid="expanded-properties-search-filter-modal-show-results"
          @click="handleClose"
          >{{ submitLabel }}</span
        >
      </div>
    </template>
  </Modal>
</template>
